/**
 * <TutorLiveCenter> — le centre du live du masteur : l'élève regardé, ses onglets, et ce qu'ils
 * montrent (organism).
 *
 * **Le contenu a la place principale** (Wajih, round 3) : au-dessus de lui, une seule ligne
 * (`<TutorLiveCenterHead>`) — l'élève regardé, les onglets, les gestes —, deux quand le centre est étroit
 * (`ONE_ROW_FROM`), et ce qui alerte le masteur (`alerts`), quand il y en a.
 *
 * **Deux familles d'onglets** (live du 04/11, Wajih : « prends du recul, trouve une manière world class ») :
 *  - **celle de l'élève**, toujours là : Visio (l'élève en grand ; tout le groupe est la mosaïque, un mode de
 *    l'écran), **Fil** — sa séance en un seul fil, ce qui s'est passé avec le champion compris (`replay`) —,
 *    **Profil** — sa fiche, au centre, là où le masteur regarde (`profile`) —, et « Contrôles » tant qu'on le
 *    regarde (`assessments`) ;
 *  - **celle de l'activité ouverte**, dans son encart, sous son titre : Tableau blanc et Quiz quand elle les
 *    a, Énoncé, Rendu (sa pastille compte les points que la correction a relevés), « Côte à côte » au bout
 *    (là où le centre en a la place, `SPLIT_FROM`), et la croix qui la ferme (`onCloseActivity`). Sans activité,
 *    l'encart n'est pas là : la rangée ne garde que l'élève.
 * Au téléphone, une seule rangée, « Activités » en tête, et la fiche reste un tiroir plein écran.
 *
 * **Changer d'élève** ne se fait pas ici (Wajih, 04/11) : c'est la pellicule, la mosaïque, ou « Voir <prénom> » dans
 * « À traiter ».
 *
 * Les gestes : l'action qu'attend le statut de l'activité (Valider, Redonner, Soumettre) en un geste,
 * « Afficher à l'élève », « Présenter », et ⋮ pour le reste — dont « Ajouter une activité », venu de la
 * pellicule. Le visage de l'élève ouvre son profil et son brief.
 *
 * « Présenter » partage ce que le centre montre, **sans la ligne de tête** : la région partagée est le
 * contenu seul (`shareRegionRef`, l'ancien recadrage du partage), dans un cadre de l'univers sous le
 * bandeau « Tu présentes » (tous deux hors de la région). Pendant une présentation, le centre n'offre que
 * ce qui se montre au groupe — l'énoncé, le rendu nu, le tableau blanc : ni la visio d'un élève, ni ses
 * contrôles, ni son quiz, ni sa liste ; sans activité à montrer, la région reste vide. L'élève du centre
 * est alors celui de l'activité présentée, quelle que soit la sélection.
 *
 * Le centre seul (`centerOnly`, l'onglet ou l'écran entier partagé, dès le départ du partage) : les
 * élèves voient aussi la tête et les alertes — la tête ne garde que ce qui sert la présentation (le titre de
 * l'activité, la barre de dessin, « Arrêter », les onglets : `presentationOnly`), aucune alerte, et la même
 * règle que pendant une présentation pour le contenu.
 *
 * Pendant une présentation, une ligne au masteur sous la tête (`present.audienceNote`, hors de la région
 * partagée) : les élèves en Concentré ne voient pas la présentation, et « Tous en Dialogue » ; jamais un
 * prénom, puisqu'au centre seul elle part aussi.
 *
 * **Hors live** (`offLive`, « Mes élèves » dans le cadre du live) : le même centre, sans visio. La famille de l'élève
 * est sa fiche d'hors live, ouverte tant qu'aucune activité ne l'est ; l'encart de l'activité est le même.
 *
 * Contrôlé : l'onglet, le côte à côte et l'activité arrivent en props ; le menu ⋮ ouvert est un état
 * d'interface. Le centre se mesure lui-même.
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { daysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types/schoolAssessment"
import {
    type UITutorLiveAlert,
    type UITutorLiveCenterTab,
    type UITutorLiveFocus,
    type UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { Button } from "../../atoms/Buttons/Button"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { ActionSheet } from "../../molecules/ActionSheet"
import { Card } from "../../molecules/Card"
import { type TabItem, Tabs } from "../../molecules/Tabs"
import { TutorLiveAlertList } from "../../molecules/TutorLiveAlertList"
import { TutorLiveActivityList } from "../../molecules/TutorLiveActivityList"
import { TutorLiveActionBar } from "../../molecules/TutorLiveActionBar"
import { TutorLiveActivityTabs } from "../../molecules/TutorLiveActivityTabs"
import {
    type TutorLiveActivityInfo,
    TutorLiveCenterHead,
    type TutorLivePresent,
} from "../../molecules/TutorLiveCenterHead"
import { TutorStudentVideo } from "../../molecules/TutorStudentVideo"
import { TutorStudentProfilePage, type TutorStudentProfilePageProps } from "../../pages/TutorStudentProfilePage"
import { TutorLiveStudentPanel, type TutorLiveStudentPanelProps } from "../TutorLiveStudentPanel"
import { TutorSessionReplay } from "../TutorSessionReplay"
import { type TutorSessionReplayProps } from "../TutorSessionReplay/TutorSessionReplay"
import { UpcomingAssessmentsSection } from "../../templates/UpcomingAssessmentsSection"
import {
    type TutorLiveActivityHandlers,
    TutorLiveActivityView,
    type TutorLiveActivityViewProps,
} from "../TutorLiveActivityView"
import { ACTIVITY_ACTIONS } from "../../molecules/TutorLiveCenterHead/tutorActivityActions"
import { tutorLiveActionBarParts } from "../../molecules/TutorLiveCenterHead/tutorLiveCenterHeadParts"
import { presentationVisual, tutorLiveCenterView, tutorLiveCenterWatched } from "./tutorLiveCenterView"

/** Les gestes sur un élève du live, son id d'abord (ceux de la pellicule). */
export type TutorLiveStudentGestures = {
    onOpenActivity: (studentId: string, activityId: string) => void
    onAddActivity: (studentId: string) => void
    onOpenProfile: (studentId: string) => void
    /** La bascule Dialogue/Concentré, au téléphone. Absente (hors live) : pas de bascule. */
    onChangeMode?: (studentId: string, mode: UISoundMode) => void
    /** Le prochain contrôle d'un élève : ouvre « Contrôles » au centre. */
    onOpenAssessments?: (studentId: string) => void
    onReorder?: (studentId: string, activityIds: string[]) => void
    onSeeMoreDone?: (studentId: string) => void
    /** L'élève dont la bascule du mode part. */
    modePendingId?: string
}

export type TutorLiveCenterProps = {
    tab: UITutorLiveCenterTab
    onChangeTab: (tab: UITutorLiveCenterTab) => void
    /** Énoncé et rendu côte à côte, quand le centre en a la place. Défaut `false`. */
    split?: boolean
    onToggleSplit?: () => void
    students: UITutorLiveStudent[]
    selectedId?: string
    renderVideo?: (participantId: string) => React.ReactNode
    /** L'activité ouverte, une activité de l'élève regardé. Absente (ou d'un autre élève) : la visio seule. */
    focus?: UITutorLiveFocus | null
    /**
     * Les gestes offerts sur l'activité, ceux de la fiche (`UITutorActivityAction`) : « Afficher à
     * l'élève » en tête, les autres dans ⋮.
     */
    actions?: UITutorActivityAction[]
    /**
     * Sous le titre de l'activité, dans la tête : son chapitre et son chrono (l'ancienne `ActivityItemInfo`).
     * Absente (défaut) : la tête d'aujourd'hui, sans la ligne de l'activité.
     */
    activityInfo?: TutorLiveActivityInfo
    onAction?: (action: UITutorActivityAction) => void
    /** Le geste qui part : son bouton (ou sa ligne) tourne. */
    submitting?: UITutorActivityAction | null
    /**
     * « Affichée chez l'élève » reste touchable : l'écran répond que l'élève l'a déjà devant lui (l'ancien
     * `ShowToStudentButton`). Défaut `false` : le bouton se grise.
     */
    showPressableWhenShown?: boolean
    /** « Présenter » et la barre de dessin, à toutes les largeurs (le téléphone compris). Absent : pas de bouton. */
    present?: TutorLivePresent
    /** Les gestes de la vue de l'activité : le visuel, les avis, « Signaler ». */
    activityHandlers?: TutorLiveActivityHandlers
    /** « Quiz d'abord · Retirer » : l'élève passe par le quiz avant l'énoncé (la forme de la fiche). */
    quizFirst?: { onRemove: () => void; removing?: boolean } | null
    /** Le calque de dessin de chaque surface (le canevas du moteur). */
    annotation?: TutorLiveActivityViewProps["annotation"]
    /**
     * La présentation partage l'onglet entier (sans recadrage), ou va le partager (le sélecteur est ouvert,
     * l'écran natif part avant que la présentation ne soit marquée) : les élèves voient tout le centre. La
     * tête ne montre plus qui est l'élève regardé (ni visage, ni prénom, ni classe), les alertes se taisent,
     * le contenu ne montre que l'activité. Défaut `false`.
     */
    centerOnly?: boolean
    /** La région que « Présenter » partage : le contenu, sans la ligne de tête (l'ancien recadrage). */
    shareRegionRef?: React.Ref<View>
    /**
     * L'onglet « Profil » est offert (un élève est regardé, hors téléphone). Défaut `false` : pas d'onglet Profil.
     */
    profileTab?: boolean
    /**
     * La fiche de l'élève regardé, que l'onglet « Profil » montre (sans ce qui n'existe pas en live : le retour,
     * « Activité », Messages). Absente alors que l'onglet est ouvert : sa forme, le temps qu'elle se lise.
     */
    profile?: (TutorLiveStudentPanelProps["fiche"] & Pick<TutorStudentProfilePageProps, "onGenerate">) | null
    /**
     * La séance de l'élève regardé, que l'onglet « Fil » relit (son fil, ligne par ligne). Absente : pas d'onglet
     * « Fil ».
     */
    replay?: TutorSessionReplayProps | null
    /** Fermer l'activité ouverte : la croix de son encart, le centre revient à l'élève. Absent : pas de croix. */
    onCloseActivity?: () => void
    /** Les contrôles de l'élève regardé, quand « Contrôles » est ouvert. */
    assessments?: {
        items: UISchoolAssessment[]
        onAdd?: () => void
        onEdit?: (id: UISchoolAssessment["id"]) => void
        onDelete?: (id: UISchoolAssessment["id"]) => void
        onDetails?: (id: UISchoolAssessment["id"]) => void
    }
    /** Ce qui alerte le masteur, au-dessus du centre. */
    alerts?: UITutorLiveAlert[]
    onDismissAlert?: (alertId: string) => void
    /**
     * Le téléphone : l'onglet Activités en tête (la liste de l'élève regardé), sa bascule dans la tête, pas
     * de côte à côte ; « Présenter » reste, comme dans l'app d'aujourd'hui. Défaut `false` : la pellicule
     * porte la liste et la bascule.
     */
    phone?: boolean
    /** Les gestes sur un élève, son id d'abord : le centre les lie à l'élève regardé (au téléphone). */
    gestures: TutorLiveStudentGestures
    /**
     * « + Activité », en tête de la barre flottante, sur chaque onglet (Wajih, live du 04/11 au soir) : le champ de
     * génération ressort, pré-rempli. Absent : pas de bouton (le téléphone le garde dans la barre du bas).
     */
    onAddActivity?: () => void
    /**
     * Hors live (« Mes élèves » dans le cadre du live) : pas d'onglet Visio, et l'onglet « Profil » montre la fiche
     * d'hors live (« Activité » compris, sans « Pour ce live »), ouverte tant qu'aucune activité ne l'est.
     * « Présenter », « Afficher à l'élève » et la bascule du mode ne viennent que de leurs props (`present`, l'action
     * `show`, `phone`), que l'écran hors live ne passe pas. Défaut `false`.
     */
    offLive?: boolean
    testID?: string
}

const TEXTS = {
    tabs: "Ce que montre le centre",
    split: "Côte à côte",
    presenting: "Tu présentes · tes élèves voient ce panneau",
    studentTabs: "Ce que montre le centre de l'élève",
    menuWhilePresenting: "Tu présentes : tes élèves voient ce menu tant qu'il est ouvert.",
    tab: {
        activities: "Activités",
        visio: "Visio",
        profile: "Profil",
        session: "Fil",
        whiteboard: "Tableau blanc",
        quiz: "Quiz",
        statement: "Énoncé",
        work: "Rendu",
        assessments: "Contrôles",
    } satisfies Record<UITutorLiveCenterTab, string>,
}

/**
 * Côte à côte à partir de cette largeur de CENTRE. L'ancienne fiche le permettait à 800 px de
 * fenêtre ; ici la pellicule et le fil prennent leur part : le seuil se lit sur le centre, seul juge
 * (avec la pellicule et le fil d'aujourd'hui : oui à 1 280 et 834, non à 1 024).
 */
const SPLIT_FROM = 560
/** Sous cette largeur de centre, les gestes de la tête prennent leur mot court (ils gardent un texte). */
const LABELS_FROM = 900
/**
 * Les gestes flottent : la tête ne porte plus que l'élève et les onglets, sur une ligne à partir de cette largeur
 * de centre (un très grand écran). En dessous — 1 440, 1 280, 1 024 —, l'élève sur sa ligne, les deux familles
 * d'onglets dessous : à 1 440, sur une ligne, l'encart de l'activité n'avait plus la place de ses vues.
 */
const ONE_ROW_FROM = 1100
/** L'action qu'attend le statut de l'activité, la première qu'elle offre : elle se garde en un geste. */
const STATUS_ACTIONS: UITutorActivityAction[] = ["validate", "redo", "submit"]
const NO_ACTIONS: UITutorActivityAction[] = []
const NO_ALERTS: UITutorLiveAlert[] = []
const noop = () => {}

export function TutorLiveCenter(props: TutorLiveCenterProps) {
    const {
        tab,
        onChangeTab,
        split = false,
        onToggleSplit,
        students,
        selectedId,
        renderVideo,
        focus,
        actions = NO_ACTIONS,
        activityInfo,
        onAction,
        submitting = null,
        showPressableWhenShown = false,
        present,
        activityHandlers,
        quizFirst = null,
        annotation,
        centerOnly = false,
        shareRegionRef,
        assessments,
        profileTab = false,
        profile,
        replay,
        onCloseActivity,
        alerts = NO_ALERTS,
        onDismissAlert,
        phone = false,
        gestures,
        onAddActivity,
        offLive = false,
        testID,
    } = props
    const roomyWidth = useWidthAtLeast(SPLIT_FROM)
    const labelsWidth = useWidthAtLeast(LABELS_FROM)
    const oneRowWidth = useWidthAtLeast(ONE_ROW_FROM)
    const roomy = roomyWidth.atLeast && !phone
    const [menuOpen, setMenuOpen] = useState(false)

    // Les élèves voient le centre : une présentation tourne ou part, ou l'onglet entier part déjà.
    const watched = tutorLiveCenterWatched({ present, centerOnly })
    const {
        student,
        ownFocus,
        keys,
        studentKeys,
        activityKeys,
        current,
        activityTab,
        body: shows,
    } = tutorLiveCenterView({
        students,
        selectedId,
        focus,
        tab,
        phone,
        presenting: watched,
        profile: profileTab,
        session: replay != null,
        offLive,
    })
    const corrected = ownFocus?.work?.hasCorrection === true ? (ownFocus.work.correctionPoints?.length ?? 0) : 0
    const itemsOf = (family: UITutorLiveCenterTab[]): TabItem<UITutorLiveCenterTab>[] =>
        family.map((key) => ({ key, label: TEXTS.tab[key], count: key === "work" ? corrected : undefined }))
    const canSplit = roomy && ownFocus != null && (current === "statement" || current === "work")
    const showsSplit = canSplit && split

    const statusAction = ownFocus != null ? STATUS_ACTIONS.find((action) => actions.includes(action)) : undefined
    // Les gestes flottent en bas du centre (Wajih, 04/11), sauf au téléphone, où la tête d'avant reste.
    const floating = !phone
    const barView =
        shows === "activity" ? (activityTab != null ? (canSplit && split ? "split" : activityTab) : null) : null
    // La barre de dessin de la barre du bas : celle de la présentation (son crayon et « Arrêter »), sinon celle du
    // tableau blanc, ouvert.
    const presentTools = present?.on === true ? present.annotation : undefined
    const boardTools = activityTab === "whiteboard" && !watched ? activityHandlers?.whiteboard : undefined
    const bar = tutorLiveActionBarParts({
        view: shows === "activity" ? barView : shows === "nothing" ? null : shows,
        focus: ownFocus != null,
        status: ownFocus?.status != null,
        primary: statusAction != null,
        show: actions.includes("show"),
        present: present != null,
        watched,
        centerOnly,
        add: student != null && onAddActivity != null,
        tools: presentTools != null || boardTools != null,
    })
    // Au téléphone, pendant une présentation, la barre de dessin prend la tête : l'action du statut repasse dans ⋮
    // (l'ancienne fiche gardait Soumettre, Valider et Redonner pendant la présentation, C-113). La barre du bas la garde
    // en un geste sur le rendu seulement : ailleurs elle est dans « Plus ».
    const headHoldsStatus = floating ? bar.primary : !(present?.on === true && present.annotation != null)
    const menuItems = (ownFocus != null ? actions : [])
        .filter((action) => action !== "show" && (action !== statusAction || !headHoldsStatus))
        .map((action) => ({
            icon: ACTIVITY_ACTIONS[action].icon,
            label: ACTIVITY_ACTIONS[action].label,
            onPress: () => onAction?.(action),
        }))

    const presenting = present?.on === true && activityTab != null
    const activityView = activityTab != null ? (showsSplit ? "split" : activityTab) : null
    // Le visuel du tableau blanc passe dans la barre de dessin pendant la présentation (hors de la région).
    const visual = presentationVisual({
        presenting: present?.on === true,
        view: activityView,
        visual: ownFocus?.visual,
        onToggle: activityHandlers?.onToggleVisual,
        onFeedback: activityHandlers?.onVisualFeedback,
    })
    // « Générer un visuel » et la bascule du visuel, sur le tableau blanc : dans la barre du bas (live du 04/11), hors
    // présentation comme pendant (la même règle que la barre de dessin de la tête au téléphone).
    const boardVisual = ownFocus?.visual
    const onGenerateVisual = activityHandlers?.onGenerateVisual
    const generateVisual =
        activityTab === "whiteboard" && onGenerateVisual != null && boardVisual?.state !== "ready"
            ? {
                  onPress: onGenerateVisual,
                  pending: boardVisual?.state === "generating",
                  retry: boardVisual?.state === "unavailable",
              }
            : undefined
    const toolsVisual = presentationVisual({
        presenting: present?.on === true || !watched,
        view: activityView,
        visual: boardVisual,
        onToggle: activityHandlers?.onToggleVisual,
        onFeedback: activityHandlers?.onVisualFeedback,
    })
    // Une seule barre de dessin : celle de la présentation (son crayon, « Arrêter »), sinon celle du tableau blanc.
    const drawTools = presentTools ?? boardTools
    const barTools =
        bar.tools && drawTools != null
            ? {
                  pen: drawTools.pen,
                  canUndo: drawTools.canUndo,
                  onUndo: drawTools.onUndo,
                  onClear: drawTools.onClear,
                  visual: toolsVisual,
                  generateVisual,
                  ...(presentTools != null && present != null
                      ? {
                            drawing: presentTools.drawing,
                            onToggleDraw: presentTools.onToggleDraw,
                            onStop: present.onToggle,
                            stopping: present.pending === true,
                        }
                      : {}),
              }
            : undefined

    let body: React.ReactNode
    if (shows === "profile") {
        // La fiche, au centre : elle défile seule, sous la ligne de l'élève ; sa forme tant qu'elle se lit.
        body =
            profile == null ? (
                <TutorStudentProfilePage profile={null} loading tab="overview" onChangeTab={noop} live={!offLive} />
            ) : offLive ? (
                // La tête du centre dit déjà qui il est : sa fiche, sans son visage ni son nom.
                <TutorStudentProfilePage {...profile} named={false} />
            ) : (
                <TutorLiveStudentPanel fiche={profile} />
            )
    } else if (shows === "session" && replay != null) {
        body = <TutorSessionReplay {...replay} />
    } else if (shows === "activities" && student != null) {
        body = (
            <ScrollView contentContainerStyle={styles.phoneList}>
                <Card p="2">
                    <TutorLiveActivityList
                        key={student.id}
                        student={student}
                        onReorder={
                            gestures.onReorder != null ? (ids) => gestures.onReorder?.(student.id, ids) : undefined
                        }
                        onSeeMoreDone={
                            gestures.onSeeMoreDone != null ? () => gestures.onSeeMoreDone?.(student.id) : undefined
                        }
                        moreDone={student.moreDone}
                        activities={student.activities}
                        openActivityId={ownFocus?.activityId}
                        onOpenActivity={(activityId) => gestures.onOpenActivity(student.id, activityId)}
                        onAddActivity={() => gestures.onAddActivity(student.id)}
                        onOpenProfile={() => gestures.onOpenProfile(student.id)}
                        size="lg"
                        defaultDoneOpen
                    />
                </Card>
            </ScrollView>
        )
    } else if (shows === "assessments" && student != null) {
        body = (
            <Card p="3">
                <UpcomingAssessmentsSection
                    assessments={assessments?.items ?? []}
                    onAddAssessment={assessments?.onAdd}
                    onEditAssessment={assessments?.onEdit}
                    onDeleteAssessment={assessments?.onDelete}
                    onAssessmentDetails={assessments?.onDetails}
                />
            </Card>
        )
    } else if (shows === "visio" && student != null) {
        // La visio : l'élève regardé, en grand (tout le groupe est la mosaïque, un mode de l'écran).
        body = (
            <View style={styles.stage}>
                <TutorStudentVideo student={student} size="stage" video={renderVideo?.(student.id)} />
            </View>
        )
    } else if (shows !== "activity" || activityTab == null || ownFocus == null) {
        // Rien à montrer (une présentation sans activité) : la région reste vide, jamais un élève.
        body = null
    } else {
        body = (
            <TutorLiveActivityView
                focus={ownFocus}
                view={showsSplit ? "split" : activityTab}
                handlers={activityHandlers}
                presenting={watched}
                roomy={roomy}
                annotation={annotation}
                toolsOutside={floating}
            />
        )
    }

    return (
        <View
            style={styles.root}
            onLayout={(event) => {
                roomyWidth.onLayout(event)
                labelsWidth.onLayout(event)
                oneRowWidth.onLayout(event)
            }}
            testID={testID}
        >
            {/* L'onglet entier part : une alerte peut nommer un élève, elle se tait. */}
            <TutorLiveAlertList alerts={centerOnly ? NO_ALERTS : alerts} onDismiss={onDismissAlert} />
            <TutorLiveCenterHead
                floating={floating}
                student={student}
                focus={ownFocus}
                primary={
                    statusAction != null && !floating
                        ? {
                              action: statusAction,
                              onPress: () => onAction?.(statusAction),
                              pending: submitting === statusAction,
                          }
                        : undefined
                }
                // Au grand écran, tous les gestes sont dans la barre du bas, présentation comprise (live du 04/11) : la
                // tête ne porte que l'élève et les onglets.
                show={
                    actions.includes("show") && !floating
                        ? {
                              onPress: () => onAction?.("show"),
                              pending: submitting === "show",
                              pressableWhenShown: showPressableWhenShown,
                          }
                        : undefined
                }
                present={floating ? undefined : present}
                onOpenMenu={menuItems.length > 0 && !floating ? () => setMenuOpen(true) : undefined}
                mode={
                    phone && student != null && gestures.onChangeMode != null
                        ? {
                              onChange: (mode) => gestures.onChangeMode?.(student.id, mode),
                              pending: student.id === gestures.modePendingId,
                          }
                        : undefined
                }
                onOpenProfile={student != null ? () => gestures.onOpenProfile(student.id) : undefined}
                objective={
                    student != null
                        ? {
                              subjects: student.objective?.subjects ?? [],
                              label: student.objective?.label,
                              assessment:
                                  student.nextAssessment != null && gestures.onOpenAssessments != null
                                      ? {
                                            daysUntil: daysUntil(student.nextAssessment.date),
                                            open: current === "assessments",
                                            onPress: () => gestures.onOpenAssessments?.(student.id),
                                        }
                                      : undefined,
                          }
                        : undefined
                }
                quizFirst={quizFirst}
                status={!phone && labelsWidth.atLeast && ownFocus?.status != null ? ownFocus.status : undefined}
                activityInfo={ownFocus != null ? activityInfo : undefined}
                compact={!labelsWidth.atLeast}
                // Dès le départ du partage (le natif diffuse avant que la présentation ne soit marquée).
                hideIdentity={centerOnly}
                // L'écran entier part : la tête ne garde que ce qui sert la présentation (tech-derisk, point 3 B).
                presentationOnly={centerOnly}
                visual={visual}
                oneLine={oneRowWidth.atLeast && !phone}
                tabs={
                    phone ? (
                        <Tabs
                            items={itemsOf(keys)}
                            value={current}
                            onChange={onChangeTab}
                            size="sm"
                            accessibilityLabel={TEXTS.tabs}
                        />
                    ) : (
                        <View style={styles.families}>
                            <View style={styles.studentFamily}>
                                <Tabs
                                    items={itemsOf(studentKeys)}
                                    value={current}
                                    onChange={onChangeTab}
                                    size="sm"
                                    fit
                                    accessibilityLabel={TEXTS.studentTabs}
                                />
                            </View>
                            {activityKeys.length > 0 && ownFocus != null && (
                                <TutorLiveActivityTabs
                                    title={ownFocus.activityTitle}
                                    items={itemsOf(activityKeys)}
                                    value={current}
                                    onChange={onChangeTab}
                                    split={
                                        canSplit && onToggleSplit != null
                                            ? { on: split, onToggle: onToggleSplit }
                                            : undefined
                                    }
                                    onClose={onCloseActivity != null && !watched ? onCloseActivity : undefined}
                                />
                            )}
                        </View>
                    )
                }
            />
            {/* Au masteur, hors de la région partagée : ce que son public ne voit pas. */}
            {present?.on === true && present.audienceNote != null && (
                <View style={styles.note}>
                    <Icon name="eye-slash" size={16} color="text-muted" />
                    <Text size="text-sm" color="text-muted" style={styles.noteText}>
                        {present.audienceNote.text}
                    </Text>
                    {present.audienceNote.action != null && (
                        <Button
                            label={present.audienceNote.action.label}
                            iconName="microphone"
                            variant="ghost"
                            size="sm"
                            onPress={present.audienceNote.action.onPress}
                        />
                    )}
                </View>
            )}
            {/* Un seul arbre, présentation ou pas : « Présenter » ne remonte pas la vue (ni ses images, ni son
                défilement) ; le cadre et le bandeau s'y ajoutent. */}
            <View style={styles.body(presenting)}>
                {presenting && (
                    <View style={styles.ribbon}>
                        <NotificationDot color="text-on-role" size={8} />
                        <Text size="text-sm" weight="bold" color="text-on-role">
                            {TEXTS.presenting}
                        </Text>
                    </View>
                )}
                {/* La région partagée : le contenu seul, sans le bandeau ni le cadre. L'espace est AUTOUR d'elle, et
                    elle porte son propre arrondi : collée au bord intérieur du cadre, un rectangle débordait sur la
                    courbe de ses coins (le teal entrait dans la région, un coin carré dépassait de l'arrondi). */}
                <View style={styles.well(presenting)}>
                    <View style={styles.inner(presenting)} ref={shareRegionRef} collapsable={false}>
                        {body}
                    </View>
                </View>
            </View>
            {floating && bar.shown && (
                // La barre du bas a sa bande à elle, SOUS le contenu, jamais par-dessus : ce qui est partagé pendant une
                // présentation est le contenu seul (`shareRegionRef`), la barre n'y entre pas — et la fin d'un rendu
                // ne passe jamais dessous.
                <View style={styles.floating}>
                    <TutorLiveActionBar
                        // Le titre de l'activité est dans son encart, en haut : la barre ne garde que son chrono.
                        info={bar.info && activityInfo?.chrono != null ? { chrono: activityInfo.chrono } : undefined}
                        tools={barTools}
                        stop={
                            bar.stop && present != null
                                ? { onPress: present.onToggle, pending: present.pending }
                                : undefined
                        }
                        status={bar.status ? ownFocus?.status : undefined}
                        primary={
                            bar.primary && statusAction != null
                                ? {
                                      action: statusAction,
                                      onPress: () => onAction?.(statusAction),
                                      pending: submitting === statusAction,
                                  }
                                : undefined
                        }
                        show={
                            bar.show
                                ? {
                                      onPress: () => onAction?.("show"),
                                      pending: submitting === "show",
                                      shown: ownFocus?.shownToStudent === true,
                                      pressableWhenShown: showPressableWhenShown,
                                  }
                                : undefined
                        }
                        present={
                            bar.present && present != null
                                ? { onPress: present.onToggle, pending: present.pending }
                                : undefined
                        }
                        onAdd={bar.add ? onAddActivity : undefined}
                        onOpenMenu={bar.menu && menuItems.length > 0 ? () => setMenuOpen(true) : undefined}
                        compact={!labelsWidth.atLeast}
                    />
                </View>
            )}
            <ActionSheet
                visible={menuOpen}
                onClose={() => setMenuOpen(false)}
                // Pendant une présentation, une feuille s'ouvre par-dessus ce qui est partagé : le masteur le sait avant
                // de choisir (live du 04/11 : rien ne se pose sur le partage sans prévenir).
                title={watched ? TEXTS.menuWhilePresenting : ownFocus?.activityTitle}
                items={menuItems}
            />
        </View>
    )
}

const PRESENT_FRAME = 3

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, minWidth: 0, gap: theme.spacing["2"] },
    /** La barre du bas : sa bande sous le contenu, centrée ; elle flotte par son ombre, jamais par sa position. */
    floating: { alignItems: "center" as const, paddingHorizontal: theme.spacing["2"] },
    /** Les deux familles d'onglets sur une rangée : l'élève à sa taille, l'activité prend le reste. */
    families: { flexDirection: "row" as const, alignItems: "center" as const, gap: theme.spacing["3"], minWidth: 0 },
    studentFamily: { flexShrink: 0 },

    stage: { flex: 1, minHeight: 0, borderRadius: theme.radius.lg, overflow: "hidden" as const },
    phoneList: { gap: theme.spacing["3"], paddingBottom: theme.spacing["4"] },
    /** Ce que voient les élèves, pendant une présentation : le cadre de l'univers, son bandeau en tête. */
    body: (presenting: boolean) => ({
        flex: 1,
        minHeight: 0,
        ...(presenting
            ? {
                  borderRadius: theme.radius.lg,
                  borderWidth: PRESENT_FRAME,
                  borderColor: theme.colors["role-fill"],
                  overflow: "hidden" as const,
              }
            : {}),
    }),
    ribbon: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
        backgroundColor: theme.colors["role-fill"],
    },
    /** La ligne du masteur pendant une présentation : discrète, elle passe à la ligne plutôt que de couper. */
    note: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
    },
    noteText: { flexShrink: 1, minWidth: 0 },
    well: (presenting: boolean) => ({ flex: 1, minHeight: 0, padding: presenting ? theme.spacing["2"] : 0 }),
    inner: (presenting: boolean) => ({
        flex: 1,
        minHeight: 0,
        ...(presenting ? { borderRadius: theme.radius.md, overflow: "hidden" as const } : {}),
    }),
}))
